import { memo, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Session, Project } from '@/api/client';
import { getMonthMatrix, toDayIso } from '@/schedule/scheduleUtils';
import {
  buildProjectIndexes,
  computeFinalSessionStatuses,
  getProjectsScheduleSplitKey,
} from '@/schedule/scheduleFinalStatus';
import { HIGHLIGHT_PARAM, HIGHLIGHT_TYPE_SESSION, useHighlightTarget } from '@/hooks/useTeleportToTarget';
import { DAY_CELL_WIDTH, DAY_CELL_HEIGHT, SCHEDULE_DAY_GAP } from '@/schedule/scheduleConstants';
import { ScheduleProvider } from '@/contexts/ScheduleContext';
import type { ScheduleHandlersContextValue, ScheduleSelectionContextValue } from '@/contexts/ScheduleContext';
import type { ScheduleSearchFilterState } from '@/schedule/scheduleSearchFilter';
import { DayCell } from './DayCell';
import './ScheduleGrid.css';

interface ScheduleGridProps {
  year: number;
  month: number;
  sessions: Session[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
  scrollReady?: boolean;
  handlersValue: ScheduleHandlersContextValue;
  selectionValue: ScheduleSelectionContextValue;
  scheduleProjects?: Project[];
  /** sessionId → число комментариев (сводка для бейджей) */
  sessionCommentCountById?: Readonly<Record<string, number>>;
  scheduleSearchFilter?: ScheduleSearchFilterState;
}

const ROW_HEIGHT = DAY_CELL_HEIGHT;
const OVERSCAN = 2;

const EMPTY_DAY_SESSIONS: Session[] = [];
const EMPTY_PROJECTS: Project[] = [];
const EMPTY_COMMENT_COUNTS: Readonly<Record<string, number>> = {};

const SCHEDULE_SEARCH_INACTIVE: ScheduleSearchFilterState = {
  active: false,
  words: [],
  matchIds: null,
};

/** Переиспользуем старый массив, если сессии не менялись — меньше ре-рендеров DayCell при 500+ сессиях */
function sessionsEqual(a: Session[], b: Session[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((s, i) => s === b[i]);
}

function nextProjectNamesEqual(
  a: string[] | null | undefined,
  b: string[] | null | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return !a && !b;
  if (a.length !== b.length) return false;
  return a.every((n, i) => n === b[i]);
}

/**
 * Кеш аннотированных объектов сессий.
 * Ключ — id сессии. Значение — аннотированный объект + ссылка на оригинал для сравнения.
 */
interface AnnotatedSession extends Session {
  _raw: Session;
  _split_ratio: number;
  _next_project_names: string[] | null | undefined;
}

function ScheduleGridInner({
  year,
  month,
  sessions,
  scrollRef,
  scrollReady = true,
  handlersValue,
  selectionValue,
  scheduleProjects = EMPTY_PROJECTS,
  sessionCommentCountById = EMPTY_COMMENT_COUNTS,
  scheduleSearchFilter,
}: ScheduleGridProps) {
  const effectiveScheduleSearch = scheduleSearchFilter ?? SCHEDULE_SEARCH_INACTIVE;
  const monthMatrix = useMemo(() => getMonthMatrix(year, month), [year, month]);

  /** Индексы проектов: пересчитываются только при изменении split-полей, не при любом project:updated. */
  const scheduleSplitKey = useMemo(
    () => getProjectsScheduleSplitKey(scheduleProjects),
    [scheduleProjects],
  );
  const projectIndexes = useMemo(
    () => buildProjectIndexes(scheduleProjects),
    // Новая ссылка на массив проектов без смены split-полей не должна пересобирать индексы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleSplitKey],
  );

  /** Кеш аннотированных объектов: sessionId → AnnotatedSession.
   * Позволяет переиспользовать один и тот же объект если сессия и её статус не изменились,
   * чтобы array-identity trick в sessionsEqual продолжал работать. */
  const annotationCacheRef = useRef<Map<string, AnnotatedSession>>(new Map());
  /** Предыдущий результат sessionsByDate для array-identity trick */
  const prevMapRef = useRef<Map<string, Session[]>>(new Map());

  const sessionsByDate = useMemo(() => {
    // 1. Единственная глобальная сортировка по (date, start).
    //    Устраняет per-group sort в computeFinalSessionStatuses и per-date sort при группировке.
    const sorted = [...sessions].sort((a, b) => {
      const da = `${(a.date as string).slice(0, 10)} ${(a.start as string).slice(0, 5)}`;
      const db = `${(b.date as string).slice(0, 10)} ${(b.start as string).slice(0, 5)}`;
      return da.localeCompare(db);
    });

    // 2. Статусы финальных/избыточных сессий. Индексы проектов берутся из отдельного useMemo.
    const finalStatuses = computeFinalSessionStatuses(sorted, projectIndexes);

    // 3. Группируем по датам — уже в хронологическом порядке (sort не нужен)
    const rawMap = new Map<string, Session[]>();
    for (const s of sorted) {
      const date = (s.date as string).slice(0, 10);
      if (!rawMap.has(date)) rawMap.set(date, []);
      rawMap.get(date)!.push(s);
    }

    // 4. Аннотируем сессии с кешированием ссылок
    const cache = annotationCacheRef.current;
    const prevMap = prevMapRef.current;
    const result = new Map<string, Session[]>();

    for (const [date, rawArr] of rawMap) {
      const annotatedArr = rawArr.map((s) => {
        const info = finalStatuses.get(s.id);
        const isFinal = info?.status === 'final';
        const isExcess = info?.status === 'excess';
        const splitRatio = info?.splitRatio ?? 1;
        const nextProjectNames = info?.nextProjectNames ?? undefined;

        const cached = cache.get(s.id);

        // Переиспользуем объект если сессия и статус не изменились
        if (
          cached &&
          cached._raw === s &&
          cached.is_final_session === isFinal &&
          cached.is_excess_session === isExcess &&
          cached._split_ratio === splitRatio &&
          nextProjectNamesEqual(cached._next_project_names, nextProjectNames)
        ) {
          return cached as Session;
        }

        const annotated: AnnotatedSession = {
          ...s,
          is_final_session: isFinal,
          is_excess_session: isExcess,
          _split_ratio: splitRatio,
          _next_project_names: nextProjectNames,
          _raw: s,
        };
        cache.set(s.id, annotated);
        return annotated as Session;
      });

      // Array-identity trick: переиспользуем прежний массив если все элементы те же
      const oldArr = prevMap.get(date);
      result.set(date, oldArr && sessionsEqual(oldArr, annotatedArr) ? oldArr : annotatedArr);
    }

    prevMapRef.current = result;
    return result;
  }, [sessions, projectIndexes]);

  const rowVirtualizer = useVirtualizer({
    count: monthMatrix.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    gap: SCHEDULE_DAY_GAP,
    overscan: OVERSCAN,
  });

  const totalHeight = rowVirtualizer.getTotalSize();

  const [searchParams] = useSearchParams();
  const rawHighlight = searchParams.get(HIGHLIGHT_PARAM);
  const highlightSessionId = rawHighlight?.startsWith(`${HIGHLIGHT_TYPE_SESSION}:`)
    ? rawHighlight.slice(HIGHLIGHT_TYPE_SESSION.length + 1)
    : null;

  const sessionForHighlight = useMemo(
    () => sessions.find((s) => s.id === highlightSessionId),
    [sessions, highlightSessionId]
  );

  useEffect(() => {
    if (!highlightSessionId || !sessionForHighlight || monthMatrix.length === 0) return;
    const dateKey = (sessionForHighlight.date as string).slice(0, 10);
    const rowIdx = monthMatrix.findIndex((weekRow) =>
      weekRow.some((dc) => toDayIso(dc) === dateKey)
    );
    if (rowIdx >= 0) rowVirtualizer.scrollToIndex(rowIdx, { align: 'center' });
  }, [highlightSessionId, sessionForHighlight, monthMatrix, rowVirtualizer]);

  const sessionHighlightReady = scrollReady && (!highlightSessionId || sessionForHighlight != null);
  useHighlightTarget('session', sessionHighlightReady, scrollRef);
  const dayCount = monthMatrix[0]?.length ?? 7;
  const gridWidth = dayCount * DAY_CELL_WIDTH + (dayCount - 1) * SCHEDULE_DAY_GAP;

  return (
    <ScheduleProvider handlers={handlersValue} selection={selectionValue}>
      <div
        className="schedule-grid"
        style={{
          width: gridWidth,
          height: `${totalHeight}px`,
          minWidth: gridWidth,
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const weekRow = monthMatrix[virtualRow.index]!;
          return (
            <div
              key={virtualRow.key}
              className="schedule-grid__row"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                height: `${virtualRow.size}px`,
              }}
            >
              {weekRow.map((dayCell) => {
                const dateIso = toDayIso(dayCell);
                const daySessions = sessionsByDate.get(dateIso) ?? EMPTY_DAY_SESSIONS;
                return (
                  <DayCell
                    key={`${dayCell.year}-${dayCell.month}-${dayCell.dayOfMonth}`}
                    dayCell={dayCell}
                    daySessions={daySessions}
                    sessionCommentCountById={sessionCommentCountById}
                    scheduleSearchFilter={effectiveScheduleSearch}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </ScheduleProvider>
  );
}

export const ScheduleGrid = memo(ScheduleGridInner);
